// dev.multinostalgia.net — the in-browser Munos IDE.
//
// Write a Munos deployment on the left, press Play, and it boots in an
// in-page emulator on the right with ZERO network round-trip: the same WASM
// compiler, MunosScriptHost, and LocalHub the real player uses, fed from the
// editor buffers instead of a server URL (see runner.ts). Everything here is
// glue — tabs, toolbar, console, ROM handling, autosave — around those two
// reused pieces (MunosEditor + DevRunner).

import { loadBrowserCompiler } from "../src/compiler.js";
import { MunosEditor, type DiagnosticSummary } from "./editor.js";
import { DevRunner, type DevMember, type LoadedRom } from "./runner.js";
import {
  loadProject, saveProject, uniqueLabel,
  type Project,
} from "./project.js";
import { importRomFile, restoreRom, lastRomRef } from "./rom.js";

const TEMPLATE_MEMBER = `client {
    event frame(frame_num: i32) {
    }
}

server {
    event tick(tick_num: i32) { }
}
`;

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`dev IDE: missing #${id}`);
  return node as T;
}

async function main(): Promise<void> {
  const project: Project = loadProject();
  let rom: LoadedRom | null = null;

  // DOM handles.
  const tabsEl = el<HTMLDivElement>("tabs");
  const editorHost = el<HTMLDivElement>("editor-host");
  const consoleEl = el<HTMLDivElement>("console");
  const canvas = el<HTMLCanvasElement>("screen");
  const playBtn = el<HTMLButtonElement>("play");
  const stopBtn = el<HTMLButtonElement>("stop");
  const loadRomBtn = el<HTMLButtonElement>("load-rom");
  const romFileInput = el<HTMLInputElement>("rom-file");
  const romLabel = el<HTMLSpanElement>("rom-label");
  const nameInput = el<HTMLInputElement>("project-name");
  const statusPill = el<HTMLSpanElement>("status-pill");

  // ---- console ----
  function logLine(kind: "info" | "error" | "connected" | "run", msg: string): void {
    const line = document.createElement("div");
    line.className = `log log-${kind}`;
    line.textContent = msg;
    consoleEl.appendChild(line);
    consoleEl.scrollTop = consoleEl.scrollHeight;
    // Keep the console from growing without bound.
    while (consoleEl.childElementCount > 400) consoleEl.firstElementChild?.remove();
  }

  // ---- boot the compiler + editor ----
  logLine("info", "booting Munos compiler…");
  const compiler = await loadBrowserCompiler();
  logLine("info", "compiler ready.");

  const runner = new DevRunner(canvas, compiler, (kind, msg) => logLine(kind, msg));

  const editor = new MunosEditor({
    parent: editorHost,
    compiler,
    onChange: (label, source) => {
      const m = project.members.find((x) => x.label === label);
      if (m) {
        m.source = source;
        saveProject(project);
      }
    },
    onDiagnostics: (summary: DiagnosticSummary) => renderDiagnostics(summary),
  });

  // ---- diagnostics surface (status pill + a pinned block in the console) ----
  let diagBlock: HTMLDivElement | null = null;
  function renderDiagnostics(summary: DiagnosticSummary): void {
    const { errorCount, messages } = summary;
    statusPill.textContent = errorCount === 0 ? "✓ compiles" : `✕ ${errorCount} error${errorCount === 1 ? "" : "s"}`;
    statusPill.className = `status-pill ${errorCount === 0 ? "ok" : "err"}`;
    if (!diagBlock) {
      diagBlock = document.createElement("div");
      diagBlock.className = "diag-block";
    }
    diagBlock.textContent = "";
    if (messages.length > 0) {
      const head = document.createElement("div");
      head.className = "diag-head";
      head.textContent = `${summary.label}: ${errorCount} error${errorCount === 1 ? "" : "s"}`;
      diagBlock.appendChild(head);
      for (const m of messages.slice(0, 12)) {
        const row = document.createElement("div");
        row.className = "diag-row";
        row.textContent = m;
        diagBlock.appendChild(row);
      }
    }
    // Pin the diagnostics block at the bottom of the console.
    if (diagBlock.parentElement !== consoleEl) consoleEl.appendChild(diagBlock);
    else consoleEl.appendChild(diagBlock);
  }

  // ---- tabs ----
  function renderTabs(): void {
    tabsEl.textContent = "";
    for (const m of project.members) {
      const tab = document.createElement("div");
      tab.className = `tab${m.label === project.activeLabel ? " active" : ""}`;

      const name = document.createElement("span");
      name.className = "tab-name";
      name.textContent = m.label;
      name.title = "double-click to rename";
      name.addEventListener("click", () => selectMember(m.label));
      name.addEventListener("dblclick", () => renameMember(m.label));
      tab.appendChild(name);

      if (project.members.length > 1) {
        const close = document.createElement("button");
        close.className = "tab-close";
        close.textContent = "×";
        close.title = "remove member";
        close.addEventListener("click", (e) => {
          e.stopPropagation();
          deleteMember(m.label);
        });
        tab.appendChild(close);
      }
      tabsEl.appendChild(tab);
    }
    const add = document.createElement("button");
    add.className = "tab-add";
    add.textContent = "+ Member";
    add.title = "add a deployment member";
    add.addEventListener("click", addMember);
    tabsEl.appendChild(add);
  }

  function selectMember(label: string): void {
    project.activeLabel = label;
    editor.showMember(label);
    saveProject(project);
    renderTabs();
    editor.focus();
  }

  function addMember(): void {
    const label = uniqueLabel(project.members);
    project.members.push({ label, source: TEMPLATE_MEMBER });
    project.activeLabel = label;
    editor.setMembers(project.members, label);
    saveProject(project);
    renderTabs();
    editor.focus();
  }

  function deleteMember(label: string): void {
    if (project.members.length <= 1) return;
    const idx = project.members.findIndex((m) => m.label === label);
    project.members = project.members.filter((m) => m.label !== label);
    if (project.activeLabel === label) {
      project.activeLabel = project.members[Math.max(0, idx - 1)]!.label;
    }
    editor.setMembers(project.members, project.activeLabel);
    saveProject(project);
    renderTabs();
  }

  function renameMember(oldLabel: string): void {
    const next = window.prompt("Rename member (its .munos label):", oldLabel);
    if (!next) return;
    const clean = next.trim().replace(/\.munos$/, "").replace(/[^A-Za-z0-9_]/g, "_");
    if (!clean || clean === oldLabel) return;
    if (project.members.some((m) => m.label === clean)) {
      logLine("error", `a member named "${clean}" already exists`);
      return;
    }
    const m = project.members.find((x) => x.label === oldLabel);
    if (!m) return;
    m.label = clean;
    editor.renameMember(oldLabel, clean);
    if (project.activeLabel === oldLabel) project.activeLabel = clean;
    saveProject(project);
    renderTabs();
  }

  // ---- ROM ----
  function showRom(): void {
    romLabel.textContent = rom ? rom.name : "no ROM";
    romLabel.classList.toggle("empty", !rom);
  }

  function pickRom(): void {
    romFileInput.click();
  }

  async function onRomPicked(): Promise<void> {
    const file = romFileInput.files?.[0];
    romFileInput.value = "";
    if (!file) return;
    try {
      rom = await importRomFile(file);
      project.rom = { hash: rom.hash, name: rom.name };
      saveProject(project);
      showRom();
      logLine("info", `loaded ROM ${rom.name}`);
    } catch (e) {
      logLine("error", `failed to load ROM: ${(e as Error).message}`);
    }
  }

  romFileInput.addEventListener("change", () => void onRomPicked());

  // ---- play / stop ----
  function currentMembers(): DevMember[] {
    return project.members.map((m) => ({ label: m.label, source: m.source }));
  }

  async function play(): Promise<void> {
    if (!rom) {
      logLine("info", "load a ROM first.");
      pickRom();
      return;
    }
    playBtn.disabled = true;
    try {
      logLine("run", `▶ running ${project.name} (${project.members.length} member${project.members.length === 1 ? "" : "s"}) on ${rom.name}`);
      await runner.play(currentMembers(), rom);
      setRunning(true);
    } catch (e) {
      logLine("error", `run failed: ${(e as Error).message}`);
      setRunning(false);
    } finally {
      playBtn.disabled = false;
    }
  }

  function stop(): void {
    runner.stop();
    setRunning(false);
    logLine("run", "■ stopped");
  }

  function setRunning(on: boolean): void {
    stopBtn.disabled = !on;
    document.body.classList.toggle("running", on);
  }

  // ---- wire the toolbar ----
  playBtn.addEventListener("click", () => void play());
  stopBtn.addEventListener("click", stop);
  loadRomBtn.addEventListener("click", () => pickRom());
  nameInput.addEventListener("input", () => {
    project.name = nameInput.value.trim() || "untitled";
    saveProject(project);
  });
  window.addEventListener("keydown", (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      void play();
    }
  });

  // ---- initial paint ----
  nameInput.value = project.name;
  editor.setMembers(project.members, project.activeLabel);
  renderTabs();
  setRunning(false);
  showRom();

  // Restore the ROM the project last ran against (or the most recent one),
  // so a reload is immediately runnable.
  const romRef = project.rom ?? lastRomRef();
  if (romRef) {
    restoreRom(romRef)
      .then((r) => {
        if (r) {
          rom = r;
          project.rom = { hash: r.hash, name: r.name };
          showRom();
          logLine("info", `restored ROM ${r.name}`);
        }
      })
      .catch(() => {/* ignore */});
  }

  logLine("info", "ready. Edit, then Play (Ctrl+Enter). Bring your own ROM.");
}

void main();
